import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

// ─────────────────────────────────────────────────────────────────────────────
// LIGHT / DARK THEME.
//
// Three states, not two: 'light', 'dark', and 'system' — and 'system' is the
// default. A visitor whose whole operating system is dark should not be handed
// a white page on the first paint just because nobody clicked anything yet.
//
// The only thing this does is put (or remove) `class="dark"` on <html>. Every
// colour in the app reads a token from src/styles/globals.css, and that file
// redefines the tokens under `.dark` — so one class swaps the entire palette
// and no component needs to know a theme exists.
//
// FIRST PAINT IS HANDLED IN index.html, not here. A small inline script applies
// the stored choice before React mounts; without it a dark-theme visitor sees a
// white flash on every page load, which is the single most visible sign of a
// theme bolted on afterwards.
// ─────────────────────────────────────────────────────────────────────────────

export const THEMES = ['light', 'dark', 'system'] as const
export type Theme = (typeof THEMES)[number]

const STORAGE_KEY = 'upbo:theme'
const DARK_QUERY = '(prefers-color-scheme: dark)'

const isTheme = (value: unknown): value is Theme =>
  typeof value === 'string' && (THEMES as readonly string[]).includes(value)

function storedTheme(): Theme {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (isTheme(saved)) return saved
  } catch {
    /* private mode / storage blocked */
  }
  return 'system'
}

const systemPrefersDark = () =>
  typeof window !== 'undefined' && window.matchMedia(DARK_QUERY).matches

interface ThemeValue {
  /** What the visitor chose, including 'system'. */
  theme: Theme
  /** What is actually on screen right now — never 'system'. */
  resolved: 'light' | 'dark'
  setTheme: (theme: Theme) => void
}

const ThemeContext = createContext<ThemeValue | null>(null)

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(storedTheme)
  const [systemDark, setSystemDark] = useState<boolean>(systemPrefersDark)

  // Follow the OS while the choice is 'system'. Subscribing unconditionally is
  // cheaper than re-subscribing on every change, and it keeps `resolved`
  // correct the instant the visitor switches back to 'system'.
  useEffect(() => {
    const media = window.matchMedia(DARK_QUERY)
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches)
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [])

  const resolved: 'light' | 'dark' = theme === 'system' ? (systemDark ? 'dark' : 'light') : theme

  useEffect(() => {
    const root = document.documentElement
    root.classList.toggle('dark', resolved === 'dark')
    // Tells the browser which palette its own widgets belong to: scrollbars,
    // form controls, the caret. Without it a dark page keeps white scrollbars.
    root.style.colorScheme = resolved
  }, [resolved])

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next)
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      /* the choice still applies to this tab */
    }
  }, [])

  const value = useMemo<ThemeValue>(() => ({ theme, resolved, setTheme }), [theme, resolved, setTheme])
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme(): ThemeValue {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider> — see src/main.tsx')
  return ctx
}
